import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cotizacionesApi } from '@/services/cotizacionesApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { TIPO_OPERACION_LABELS, ESTADO_COTIZACION_LABELS, ESTADO_COLORS } from '@/utils/constants';
import { generateReportePdf } from '@/utils/reportePdfGenerator';

const today = new Date().toISOString().slice(0, 10);
const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export function TodasCotizaciones() {
  const [page, setPage] = useState(0);
  const [desde, setDesde] = useState(firstOfMonth);
  const [hasta, setHasta] = useState(today);
  const [generando, setGenerando] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['all-cotizaciones', page],
    queryFn: () => cotizacionesApi.findAll(page, 10),
  });

  const handleGenerarReporte = async () => {
    setGenerando(true);
    try {
      const cots = await cotizacionesApi.getReporte(desde, hasta);
      if (cots.length === 0) {
        alert('No hay cotizaciones en ese rango de fechas.');
        return;
      }
      generateReportePdf(cots, desde, hasta);
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Todas las Cotizaciones</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <button
            onClick={handleGenerarReporte}
            disabled={generando}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {generando ? 'Generando...' : 'Descargar Reporte PDF'}
          </button>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cotizador</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : data?.content.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No hay cotizaciones</td></tr>
            ) : (
              data?.content.map((cot) => (
                <tr key={cot.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{cot.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(cot.fechaCreacion)}</td>
                  <td className="px-4 py-3 text-sm">{cot.cliente.nombre}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{cot.usuarioNombre}</td>
                  <td className="px-4 py-3 text-sm">{TIPO_OPERACION_LABELS[cot.tipoOperacion]}</td>
                  <td className="px-4 py-3 text-sm font-medium">{formatCurrency(cot.totalFinal)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[cot.estadoCotizacion]}`}>
                      {ESTADO_COTIZACION_LABELS[cot.estadoCotizacion]}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : data?.content.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay cotizaciones</p>
        ) : (
          data?.content.map((cot) => (
            <div key={cot.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">#{cot.id}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[cot.estadoCotizacion]}`}>
                  {ESTADO_COTIZACION_LABELS[cot.estadoCotizacion]}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900">{cot.cliente.nombre}</p>
              <p className="text-xs text-gray-500 mt-0.5">{cot.usuarioNombre} · {TIPO_OPERACION_LABELS[cot.tipoOperacion]}</p>
              <div className="flex items-center justify-between mt-2 text-sm">
                <span className="text-gray-500">{formatDate(cot.fechaCreacion)}</span>
                <span className="font-bold">{formatCurrency(cot.totalFinal)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between px-1 sm:px-4 py-3 mt-3 sm:mt-0 sm:border-t border-gray-200">
          <span className="text-sm text-gray-500">Pág. {data.number + 1} de {data.totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={data.number === 0} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Anterior</button>
            <button onClick={() => setPage((p) => p + 1)} disabled={data.number >= data.totalPages - 1} className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Siguiente</button>
          </div>
        </div>
      )}
    </div>
  );
}
